/* InfoTip — jediná komponenta pro „i“ (DESIGN-PRAVIDLA §1).
 * ============================================================================
 *   <InfoTip nazev="Fixace" text="Doba, po kterou se sazba nemění." />
 *   <InfoTip nazev="Jak se počítá"><ul>…</ul></InfoTip>
 *
 * Props:
 *   nazev    (povinné) název věci; jde do aria-label „Nápověda: {nazev}“ a jako tučný nadpis
 *   text     krátké vysvětlení (max. 2–3 věty)
 *   children bohatší obsah (odrážky, odkaz) — místo `text` nebo pod něj
 *   strana   "auto" | "top" | "bottom" (výchozí auto = pod, nahoře když není místo)
 *   velikost velikost ikony v px (výchozí 14)
 *   className, testid (výchozí "info-tip")
 *
 * Chování: klik i dotyk přepíná; na desktopu (myš) se otevře i po ~300 ms najetí
 * a zavře po opuštění; klávesnicí Enter/mezerník nebo fokus z klávesnice (focus-visible).
 * Esc zavře a vrátí fokus na tlačítko (Radix). Klik z otevřeného najetím panel „připne“.
 * Plocha tlačítka 44 × 44 px, záporný okraj ji schová do řádku.
 * Pohyb: fade + 4 px, 150 ms, EASE_OUT; omezený pohyb = okamžitě.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { motion } from "motion/react";
import { Info } from "lucide-react";
import { RYCHLE, EASE_OUT, useBezPohybu } from "@/lib/pohyb";
import { useTextZakladu } from "@/components/ui/zakladTexty";

const PRODLEVA_OTEVRENI_MS = 300;
const PRODLEVA_ZAVRENI_MS = 150;

export default function InfoTip({ nazev, text, children, strana = "auto", velikost = 14, className = "", testid = "info-tip" }) {
  const tz = useTextZakladu();
  const bezPohybu = useBezPohybu();
  const [otevreno, setOtevreno] = useState(false);
  const hoverem = useRef(false); // otevřeno najetím (ne klikem)
  const casovac = useRef(null);
  const potlacitFokus = useRef(false); // fokus vrácený po Esc nemá znovu otevřít

  const zrusCasovac = () => { if (casovac.current) { clearTimeout(casovac.current); casovac.current = null; } };
  useEffect(() => zrusCasovac, []);

  const otevri = useCallback((zHoveru) => { zrusCasovac(); hoverem.current = !!zHoveru; setOtevreno(true); }, []);
  const zavri = useCallback(() => { zrusCasovac(); hoverem.current = false; setOtevreno(false); }, []);

  const naKlik = (e) => {
    e.preventDefault(); // přepínání řídíme sami (Radix by panel otevřený najetím zavřel)
    e.stopPropagation(); // klik na „i“ v klikací hlavičce ji nesmí přepnout
    if (otevreno && hoverem.current) { hoverem.current = false; return; } // připnout
    if (otevreno) zavri(); else otevri(false);
  };
  const naNajeti = (e) => {
    if (e.pointerType !== "mouse" || otevreno) { zrusCasovac(); return; }
    zrusCasovac();
    casovac.current = setTimeout(() => otevri(true), PRODLEVA_OTEVRENI_MS);
  };
  const naOdjeti = (e) => {
    if (e.pointerType !== "mouse") return;
    zrusCasovac();
    if (otevreno && hoverem.current) casovac.current = setTimeout(zavri, PRODLEVA_ZAVRENI_MS);
  };
  /* Enter z klávesnice přepíná přímo (preventDefault zruší syntetický klik,
     jinak by se panel otevřel a hned zavřel). Mezerník nechává prohlížeči. */
  const naKlavesu = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    e.stopPropagation();
    if (otevreno && hoverem.current) { hoverem.current = false; return; }
    if (otevreno) zavri(); else otevri(false);
  };
  const naFokus = (e) => {
    if (potlacitFokus.current) { potlacitFokus.current = false; return; }
    let zKlavesnice = false;
    try { zKlavesnice = e.currentTarget.matches(":focus-visible"); } catch { zKlavesnice = false; }
    if (zKlavesnice && !otevreno) otevri(true);
  };

  const side = strana === "top" ? "top" : "bottom";
  const avoidCollisions = strana === "auto";
  const plocha = 44;
  const okraj = -Math.round((plocha - velikost) / 2);

  return (
    <Popover.Root open={otevreno} onOpenChange={(v) => (v ? otevri(false) : zavri())}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={`${tz("napoveda")}: ${nazev}`}
          data-testid={testid}
          data-otevreno={otevreno ? "1" : "0"}
          onClick={naKlik}
          onKeyDown={naKlavesu}
          onPointerEnter={naNajeti}
          onPointerLeave={naOdjeti}
          onFocus={naFokus}
          className={`group relative inline-flex shrink-0 items-center justify-center rounded-full align-middle text-[var(--muted)] transition-colors duration-150 hover:text-[var(--accent)] focus-visible:text-[var(--accent)] data-[state=open]:text-[var(--accent)] ${className}`}
          style={{ width: plocha, height: plocha, margin: okraj, cursor: "help", lineHeight: 0 }}
        >
          <Info size={velikost} strokeWidth={2.2} aria-hidden="true" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side={side}
          align="center"
          sideOffset={-6}
          collisionPadding={12}
          avoidCollisions={avoidCollisions}
          onPointerEnter={(e) => { if (e.pointerType === "mouse") zrusCasovac(); }}
          onPointerLeave={naOdjeti}
          onCloseAutoFocus={() => { potlacitFokus.current = true; setTimeout(() => { potlacitFokus.current = false; }, 0); }}
          onOpenAutoFocus={(e) => e.preventDefault()}
          data-testid={`${testid}-panel`}
          aria-label={nazev}
          className="z-[1000] outline-none"
        >
          <motion.div
            initial={bezPohybu ? false : { opacity: 0, y: side === "top" ? 4 : -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={bezPohybu ? { duration: 0 } : { duration: RYCHLE, ease: EASE_OUT }}
            className="w-[min(300px,calc(100vw-24px))] rounded-[var(--radius)] border border-[var(--line)] bg-[var(--surface)] p-3 text-left text-sm text-[var(--ink)] shadow-[var(--shadow-float)]"
          >
            {nazev ? <div className="mb-1 font-semibold">{nazev}</div> : null}
            {text ? <p className="text-[var(--muted)]">{text}</p> : null}
            {children ? <div className={text ? "mt-2" : ""}>{children}</div> : null}
          </motion.div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
